import { useState } from "react";
import { Sparkles } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@app/components/ui/popover";
import { Trans, useLingui } from "@lingui/macro";

export function ReliabilityInfoButton() {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  return (
    <>
      <style>{`
        @keyframes gradient-shift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes sparkle-spin {
          0%   { transform: rotate(0deg) scale(1); }
          25%  { transform: rotate(15deg) scale(1.15); }
          50%  { transform: rotate(0deg) scale(1); }
          75%  { transform: rotate(-15deg) scale(1.15); }
          100% { transform: rotate(0deg) scale(1); }
        }
        .reliability-btn {
          background: linear-gradient(135deg, #1752B7, #3b82f6, #6366f1, #1752B7);
          background-size: 250% 250%;
          animation: gradient-shift 4s ease infinite;
        }
        .reliability-btn:hover .sparkle-icon {
          animation: sparkle-spin 0.6s ease-in-out;
        }
      `}</style>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="reliability-btn fixed bottom-6 right-6 z-50 h-13 w-13 rounded-full shadow-lg shadow-primary/40 flex items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            style={{ height: 52, width: 52 }}
            aria-label={t`Fiabilité de la plateforme`}
          >
            <Sparkles className="sparkle-icon h-5 w-5 text-white drop-shadow" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="end"
          sideOffset={12}
          className="w-80 p-4 mb-1"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <h4 className="text-sm font-semibold text-slate-900">
              <Trans>Fiabilité de la plateforme</Trans>
            </h4>
            <span className="shrink-0 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
              95% F1
            </span>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed mb-3">
            <Trans>
              Notre technologie transforme vos notes médicales non structurées
              en données structurées avec un taux de fiabilité moyen de{" "}
              <strong>95%</strong> (score F1) sur l'ensemble des champs d'une
              ontologie.
            </Trans>
          </p>
          <p className="text-xs font-medium text-slate-700 mb-1.5">
            <Trans>Les 5% d'erreurs restantes sont de deux types :</Trans>
          </p>
          <ul className="text-xs text-slate-600 space-y-1.5 mb-3">
            <li className="flex gap-2">
              <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-slate-400 mt-1.5" />
              <span>
                <strong>
                  <Trans>Faux négatif</Trans>
                </strong>
                <Trans>
                  {" "}
                  — l'information était présente mais n'a pas été détectée
                  (champ laissé vide).
                </Trans>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-slate-400 mt-1.5" />
              <span>
                <strong>
                  <Trans>Faux positif</Trans>
                </strong>
                <Trans>
                  {" "}
                  — une valeur erronée a été renseignée pour un champ.
                </Trans>
              </span>
            </li>
          </ul>
          <p className="text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-2.5">
            <Trans>
              Le score F1 combine la <strong>précision</strong> (« ce que j'ai
              trouvé est-il juste ? ») et le <strong>rappel</strong> (« ai-je
              oublié des informations ? »).
            </Trans>
          </p>
        </PopoverContent>
      </Popover>
    </>
  );
}
